import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'

import { getGridSingleSelectOperators, GridColDef } from '@mui/x-data-grid-premium'

import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useResponsive from '@/components/hooks/useResponsive'
import { getOrganization } from '@/store/auth/authSlice'
import i18next, { t } from 'i18next'
import { useSelector } from 'react-redux'


import { jobBoardsQueryOptions } from '@/api/jobBoardsQueryOptions'
import { defaultJob, jobSchema, JobType } from '@/api/jobs'
import { medusaJobsQueryOptions, organizationJobsQueryOptions, useCreateJobMutation, useDeleteJobMutation, useUpdateJobMutation } from '@/api/jobsQueryOptions'
import { OrganizationLevelType } from '@/api/organizations'
import { sectionsAvailableQueryOptions } from '@/api/sectionsQueryOptions'
import CreateDetailAccordion, { useCreateDetail } from '@/components/detail/CreateDetailRecord'
import AbbreviationColumn from '@/components/grid/AbbreviationColumn'
import NameColumn from '@/components/grid/NameColumn'
import useOrganizationDetailView from '@/components/hooks/useOrganizationDetailView'
import ScoregCheckbox from '@/components/mui/ScoregCheckbox'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import SelectBoard from '@/components/select/SelectBoard'
import SelectMedusaJob from '@/components/select/SelectMedusaJob'
import SelectSection from '@/components/select/SelectSection'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid } from '@mui/material'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/jobs',
)({
    component: OrganizationComponent,
})

function OrganizationComponent() {
    const params = Route.useParams()

    const { query, data, createable, updateMutation, deleteMutation, onSubmit } = useOrganizationDetailView<JobType>(params, organizationJobsQueryOptions,
        'Job', useCreateJobMutation, useUpdateJobMutation, useDeleteJobMutation)

    const jobBoardsQuery = useSuspenseQuery(jobBoardsQueryOptions())
    const jobBoards = jobBoardsQuery.data

    const sectionsQuery = useSuspenseQuery(sectionsAvailableQueryOptions())
    const sections = sectionsQuery.data

    const medusaJobsQuery = useSuspenseQuery(medusaJobsQueryOptions())
    const medusaJobs = medusaJobsQuery.data

    const sessionOrganization = useSelector(getOrganization)

    const basicGridColumns = useBasicGridColumns()

    const { large, xlarge } = useResponsive()

    const columns: GridColDef[] = []

    columns.push(
        AbbreviationColumn(),
        NameColumn(100),
        {
            field: 'organizationLevel',
            type: 'singleSelect',
            valueOptions: [
                { value: OrganizationLevelType.TROOP, label: t('enum.organizationLevel.TROOP') },
                { value: OrganizationLevelType.DIVISION, label: t('enum.organizationLevel.DIVISION') },
                { value: OrganizationLevelType.FEDERATION, label: t('enum.organizationLevel.FEDERATION') },
            ],
            valueFormatter: (_params, row) => {
                if (row.organizationLevel) {
                    return t(`enum.organizationLevel.${row.organizationLevel}`)
                }
                return ''
            },
            minWidth: 100,
            headerName: t('property.job.organizationLevel'),
            editable: true,
        },
        {
            field: 'board',
            headerName: i18next.t('property.job.board'),
            type: 'singleSelect',
            valueOptions: jobBoards,
            getOptionValue: (option: any) => option.uuid,
            getOptionLabel: (option: any) => option.name,
            valueGetter: (_value, row) => row.board.uuid,
            valueFormatter: (_value, row) => row.board?.name,
            valueSetter: (value, row) => {
                return { ...row, board: { uuid: value } }
            },
            filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'isAnyOf'),
            editable: true,
        },
        {
            field: 'section',
            headerName: i18next.t('property.job.section'),
            type: 'singleSelect',
            valueOptions: sections,
            getOptionValue: (option: any) => option.uuid,
            getOptionLabel: (option: any) => option.name,
            valueGetter: (_value, row) => row.section?.uuid || '',
            valueFormatter: (_value, row) => row.section?.name || '',
            valueSetter: (value, row) => {
                return { ...row, section: { uuid: value } }
            },
            filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'isAnyOf'),
            editable: true,
        },
    )

    if ((large || xlarge) && medusaJobs.length > 0) {
        columns.push(
            {
                field: 'medusaJob',
                headerName: i18next.t('property.job.medusaJob'),
                type: 'singleSelect',
                valueOptions: medusaJobs,
                getOptionValue: (option: any) => option.uuid,
                getOptionLabel: (option: any) => option.name,
                valueGetter: (_value, row) => row.medusaJob?.uuid || '',
                valueFormatter: (_value, row) => row.medusaJob?.name || '',
                valueSetter: (value, row) => {
                    return { ...row, medusaJob: { uuid: value } }
                },
                filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'isAnyOf'),
                editable: true,
            })
    }
    if (large || xlarge) {
        columns.push(
            {
                field: 'educationApprover',
                headerName: i18next.t('property.job.educationApprover'),
                type: 'boolean',
                editable: true,
            }
        )

        columns.push(
            basicGridColumns.startDateColumn,
            basicGridColumns.endDateColumn,
            basicGridColumns.descriptionColumn
        )
    }

    const createDetail = useCreateDetail<JobType>({
        onSubmit,
        resolver: yupResolver<JobType, any, any>(jobSchema),
        defaultValues: {
            ...defaultJob,
            organization: {
                uuid: params.organizationUuid,
            },
        },
        title: t('action.new', { entity: t('entity.job_other') }),
    })

    const editGrid = useEditGrid<JobType>({
        defaultValues: defaultJob,
        updateMutation: updateMutation,
        deleteMutation: deleteMutation,
        records: data,
        columns: columns,
        title: t('entity.job'),
    })

    if (query.isLoading || query.isFetching) {
        return <LoadProgress />
    }

    if (jobBoardsQuery.isLoading || jobBoardsQuery.isFetching) {
        return <LoadProgress />
    }

    if (sectionsQuery.isLoading || sectionsQuery.isFetching) {
        return <LoadProgress />
    }

    if (medusaJobsQuery.isLoading || medusaJobsQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            <Typography>
                {t('entity.job_other')}
            </Typography>
            {createable && (
                <CreateDetailAccordion {...createDetail}>
                    <Grid container spacing={2}>
                        <input
                            type="hidden"
                            defaultValue={params.organizationUuid}
                            {...createDetail.form.register('organization.uuid')}
                        />
                        <input
                            type="hidden"
                            defaultValue={sessionOrganization.organizationLevel}
                            {...createDetail.form.register('organizationLevel')}
                        />
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={defaultJob.name}
                                required
                                {...createDetail.form.register('name')}
                                label={i18next.t('property.general.name')}
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                helperText={createDetail.form.formState.errors.name?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 4,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={defaultJob.abbreviation}
                                required
                                {...createDetail.form.register('abbreviation')}
                                label={i18next.t('property.general.abbreviation')}
                                slotProps={{ htmlInput: { maxLength: 10 } }}
                                helperText={createDetail.form.formState.errors.abbreviation?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectBoard control={createDetail.form.control} name="board.uuid" defaultValue={defaultJob.board} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectSection control={createDetail.form.control} name="section.uuid" optional={true} excludeCombined={true} />
                        </Grid>
                    </Grid>
                    {medusaJobs.length > 0 && (
                        <Grid container spacing={2}>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 4
                                }}>
                                <SelectMedusaJob control={createDetail.form.control} name="medusaJob.uuid" defaultValue={defaultJob.medusaJob} />
                            </Grid>
                        </Grid>
                    )}
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 10,
                                lg: 6
                            }}>
                            <TextField
                                defaultValue={defaultJob.description}
                                {...createDetail.form.register('description')}
                                label={i18next.t('property.general.description')}
                                slotProps={{ htmlInput: { maxLength: 1000 } }}
                                helperText={createDetail.form.formState.errors.description?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        {/* <Grid
                            size={{
                                xs: 6,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregCheckbox
                                name="leader"
                                defaultValue={defaultJob.leader}
                                control={createDetail.form.control}
                                label={t('property.job.leader')} />
                        </Grid> */}
                        <Grid
                            size={{
                                xs: 6,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregCheckbox
                                name="educationApprover"
                                defaultValue={defaultJob.educationApprover}
                                control={createDetail.form.control}
                                label={t('property.job.educationApprover')} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregDatePicker
                                name="startDate"
                                defaultValue={dayjs(defaultJob.startDate)}
                                disabled={false}
                                control={createDetail.form.control}
                                label={t('property.general.startDate')}
                            />
                        </Grid>
                        <></>
                    </Grid>
                </CreateDetailAccordion>
            )}
            <EditGrid {...editGrid}></EditGrid>
        </>
    )
}
